import { useEffect, useState } from "react";

/**
 * A clock that advances, so age labels decay on their own.
 *
 * Without this, `relativeTime` is only ever evaluated during a render, and
 * nothing re-renders between fetches -- a device polled ten seconds before
 * page load reads "NOW" an hour later. Ticking separately from the data
 * refresh also means the label keeps telling the truth even when refreshes
 * are failing.
 *
 * The default tick is finer than it strictly needs to be because the labels
 * now show seconds: a 15s tick would leave a "32s ago" reading up to 15s
 * wrong, and make a counter that is meant to look alive advance in visible
 * jumps.
 */
export function useNow(intervalMs = 5_000): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = () => setNow(Date.now());
    const timer = setInterval(tick, intervalMs);
    // A backgrounded tab throttles timers and a sleeping machine stops them
    // outright, so the clock can be arbitrarily far behind on return. Resync
    // at the moment it becomes visible, which is exactly when it is read.
    document.addEventListener("visibilitychange", tick);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [intervalMs]);

  return now;
}
